import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import { StandardButton } from 'base/StandardButton/StandardButton';
import SearchBox from 'components/common/SearchBox';
import { ROLE_MATRIX_FORM, ROLE_MATRIX_FORM_ID } from 'constant/RoutesUrls';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { capitalizeFirstLetter, checkAccess, showValue } from 'utils/helpers';
import { RolePageInterface, paramsI } from '../../../types/interface';
import { useAppDispatch } from '../../redux/HooksRedux';
import * as roleAction from '../../redux/role/roleAction';
import {
  errorNotification,
  successNotification
} from 'base/notifications/Notifications';
import { ColumnDef } from '@tanstack/react-table';
import { UilEllipsisV } from '@iconscout/react-unicons';
import useAdvanceTable from 'hooks/useAdvanceTable';
import DeleteModel from 'base/DeleteModal/DeleteModal';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { StandardLoader } from 'base/Loader/Loader';
import { SortingState } from '@tanstack/react-table';
import {
  ACCENDING,
  DESCENDING,
  GERMAN,
  modules,
  SELECTED_LANGUAGE,
  ViewAllLimit
} from 'utils/enums/misc';
import { debounceTime } from 'utils/staticData';
import CustomAdvanceTableFooter from 'components/customComponent/CustomAdvanceTableFooter';
import { getCookie } from 'utils/cookieUtils';

export const Role = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { totalPages, totalRecords } = useSelector(
    (state: RootState) => state?.roleSlice || 0
  );
  const userPermission = useSelector(
    (state: RootState) => state.userProfile.permission
  );

  const [searchText, setSearchText] = useState('');
  const [pageCount, setPageCount] = useState<number>(1);
  const [isAllVisibleData, setIsAllVisibleData] = useState(false);
  const [limit, setLimit] = useState<number>(10);
  const [roleList, setRoleList] = useState<RolePageInterface[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>();
  const [deleteRoleData, setDeleteRoleData] = useState<{
    role_id: number;
    showDeletePopup: boolean;
  }>({
    role_id: 0,
    showDeletePopup: false
  });
  const currentLanguage = getCookie(SELECTED_LANGUAGE);

  const getAllRole = async () => {
    const params: paramsI = {
      page: pageCount,
      limit: isAllVisibleData ? ViewAllLimit : limit,
      keyword: searchText,
      sortBy: sorting?.[0]?.id,
      sortOrder: sorting?.[0]?.desc ? DESCENDING : ACCENDING
    };
    setIsLoading(true);
    try {
      const response = await dispatch(roleAction.getAllRoles(params));

      if (response?.status === 200) {
        setRoleList(response.data?.data?.roles || []);
        if (pageCount > response?.data?.meta?.totalPages) {
          setPageCount(1);
        }
      } else {
        errorNotification(response?.data?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (editRoleId: number) => {
    navigate(
      `${ROLE_MATRIX_FORM_ID.replace(':editRoleId', editRoleId?.toString())}`
    );
  };

  const debouncedSearch = useCallback(() => {
    const handler = setTimeout(() => {
      getAllRole();
    }, debounceTime);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText, pageCount, isAllVisibleData, sorting, limit]);

  useEffect(() => {
    setIsLoading(true);
    const cleanup = debouncedSearch();
    return cleanup;
  }, [searchText, pageCount, isAllVisibleData, sorting, limit]);

  const columns: ColumnDef<RolePageInterface>[] = [
    {
      id: 'role_id',
      accessorKey: t('applications.table.title_1'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('applications.table.title_1')}</span>
      ),
      cell: ({ row: { index } }) => <div className="py-2">{index + 1}</div>
    },
    {
      id: 'role_name',
      accessorKey: t('role.table.text_1'),
      header: () => (
        <span className="text-nowrap">{t('role.table.text_1')}</span>
      ),
      cell: ({ row: { original } }) => {
        return (
          <div className="d-flex align-items-center">
            <ConditionalRender
              condition={currentLanguage === GERMAN}
              fallBack={
                <p className="mb-0">{showValue(original?.role_name)}</p>
              }
            >
              <p className="mb-0">{showValue(original?.role_name_de)}</p>
            </ConditionalRender>
          </div>
        );
      }
    },
    {
      id: 'action',
      accessorKey: checkAccess(
        [modules.ROLE.edit, modules.ROLE.delete],
        userPermission
      )
        ? t('general.common_text.text_8')
        : '',
      enableSorting: false,
      header: () =>
        checkAccess(
          [modules.ROLE.edit, modules.ROLE.delete],
          userPermission
        ) ? (
          <span className="text-nowrap">{t('general.common_text.text_8')}</span>
        ) : (
          ''
        ),
      cell: ({ row: { original } }) => (
        <ConditionalRender
          condition={checkAccess(
            [modules.ROLE.edit, modules.ROLE.delete],
            userPermission
          )}
        >
          <Dropdown
            id="scroll_in_table"
            placement="auto"
            className="position-static"
          >
            <Dropdown.Toggle
              variant="link"
              className="no-padding no-margin ps-0 d-flex"
            >
              <UilEllipsisV className="text-body" />
            </Dropdown.Toggle>

            <Dropdown.Menu className="p-0">
              <ConditionalRender
                condition={checkAccess([modules.ROLE.edit], userPermission)}
              >
                <Dropdown.Item
                  className="pe-0 border-bottom"
                  onClick={() => handleEdit(original?.role_id)}
                >
                  <span className="d-flex fs-16 p-0 font-normal">
                    <p className="mb-0">
                      {capitalizeFirstLetter(t('general.common_text.text_13'))}
                    </p>
                  </span>
                </Dropdown.Item>
              </ConditionalRender>
              <ConditionalRender
                condition={checkAccess(['role.delete'], userPermission)}
              >
                <Dropdown.Item
                  onClick={() => {
                    setDeleteRoleData({
                      role_id: original?.role_id,
                      showDeletePopup: true
                    });
                  }}
                >
                  <span className="d-flex fs-16 font-normal p-0">
                    <p className="mb-0">
                      {capitalizeFirstLetter(t('general.common_text.text_14'))}
                    </p>
                  </span>
                </Dropdown.Item>
              </ConditionalRender>
            </Dropdown.Menu>
          </Dropdown>
        </ConditionalRender>
      )
    }
  ];

  const table = useAdvanceTable({
    data: roleList || [],
    columns: columns,
    selection: false,
    sortable: true,
    pageSize: limit,
    pagination: true,
    sorting: sorting,
    setSorting: setSorting
  });

  const handleDeleteProduct = async () => {
    try {
      const response = await dispatch(
        roleAction.deleteRole(deleteRoleData.role_id)
      );
      if (response?.status === 200) {
        successNotification(response.data?.message);
        setDeleteRoleData({ role_id: 0, showDeletePopup: false });
        getAllRole();
      } else {
        errorNotification(response?.data?.message);
      }
    } catch (error) {
      setDeleteRoleData({ role_id: 0, showDeletePopup: false });
      console.log(error);
    }
  };

  const renderDeletePopup = () => {
    if (deleteRoleData.showDeletePopup) {
      return (
        <DeleteModel
          onCancel={() =>
            setDeleteRoleData({ role_id: 0, showDeletePopup: false })
          }
          onConfirm={handleDeleteProduct}
        />
      );
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between mb-2 align-items-center flex-column flex-sm-row">
        <div className="w-auto">
          <h3>{capitalizeFirstLetter(t('header.text.text_8'))} </h3>
        </div>
        <div className="d-flex gap-2 w-auto flex-column flex-sm-row">
          <SearchBox
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <ConditionalRender
            condition={checkAccess([modules.ROLE.create], userPermission)}
          >
            <StandardButton
              buttonText={capitalizeFirstLetter(t('role.button.text_1'))}
              onClick={() => {
                navigate(ROLE_MATRIX_FORM);
              }}
            />
          </ConditionalRender>
        </div>
      </div>
      <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
        <div className="px-4 py-1 px-lg-6 bg-body-emphasis  rounded border-top border-bottom border-left border-right border position-relative top-1">
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              tableProps={{
                className: 'phoenix-table fs-9 p-0',
                responsive: true
              }}
              dataLength={roleList.length}
            />
            <ConditionalRender condition={totalPages > 0}>
              <CustomAdvanceTableFooter
                pagination
                totalPages={totalPages}
                totalRecords={totalRecords}
                recordsPerPage={limit}
                currentPage={pageCount}
                setCurrentPage={setPageCount}
                CanPreviousPage={() => pageCount > 1}
                CanNextPage={() => totalPages > pageCount}
                handleNextPage={() => setPageCount(pageCount + 1)}
                handlePreviousPage={() => setPageCount(pageCount - 1)}
                showViewAllBtn={true}
                isAllVisibleData={isAllVisibleData}
                setIsAllVisibleData={setIsAllVisibleData}
                setRecordsPerPage={setLimit}
                isLoading={isLoading}
              />
            </ConditionalRender>
          </AdvanceTableProvider>
        </div>
      </ConditionalRender>
      {renderDeletePopup()}
    </>
  );
};
